import { useState, useEffect, useMemo } from 'react';
import { MdPerson, MdEdit, MdPeople, MdWork, MdAttachMoney, MdCheckCircle, MdStar, MdMessage, MdCalendarToday, MdPayment, MdCardMembership } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getUser, getAuthToken, saveAuth } from '../../services/auth';
import api from '../../services/api';
import { t } from '../../utils/i18n';

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    earnings: 0,
    completed: 0,
    rating: 0
  });
  const [jobs, setJobs] = useState([]);
  const [friends, setFriends] = useState([]);
  const [unpaidDebt, setUnpaidDebt] = useState(null);
  const [manualPaymentDates, setManualPaymentDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isParent = user?.role === 'parent';
  const activeBabysitters = useMemo(() => (
    friends.filter(f => f.relationshipType === 'babysitter' && f.hasAccount).length
  ), [friends]);

  const userJobs = useMemo(() => {
    if (!user?.id) return [];
    return jobs.filter(job =>
      job.parentId === user.id ||
      job.babysitterId === user.id ||
      job.serviceUserId === user.id ||
      job.serviceProviderId === user.id
    );
  }, [jobs, user]);

  const activeJobsCount = useMemo(() => (
    userJobs.filter(j => j.status === 'accepted' || j.status === 'in_progress').length
  ), [userJobs]);

  const nextManualPayment = useMemo(() => {
    const now = new Date();
    return (manualPaymentDates || [])
      .map((entry) => {
        if (!entry?.date) return null;
        const [y, m, d] = entry.date.split('-').map(Number);
        const [hh, mm] = (entry.time || '14:00').split(':').map(Number);
        const dateObj = new Date(y, m - 1, d, hh || 14, mm || 0, 0, 0);
        return Number.isNaN(dateObj.getTime()) ? null : dateObj;
      })
      .filter(Boolean)
      .filter((dateObj) => dateObj >= now)
      .sort((a, b) => a - b)[0] || null;
  }, [manualPaymentDates]);

  const quickActions = useMemo(() => {
    if (isParent) {
      return [
        { label: t('profile.friends') || 'Prijatelji', icon: MdPeople, onClick: () => navigate('/dashboard/friends') },
        { label: t('profile.payments') || 'Plaćanja', icon: MdPayment, onClick: () => navigate('/dashboard/payments') },
        { label: t('profile.listings') || 'Oglasi', icon: MdWork, onClick: () => navigate('/dashboard/jobs') },
        { label: t('profile.messages') || 'Poruke', icon: MdMessage, onClick: () => navigate('/dashboard/conversations') },
      ];
    }
    return [
      { label: t('profile.listings') || 'Oglasi', icon: MdWork, onClick: () => navigate('/dashboard/jobs') },
      { label: t('profile.editProfile') || 'Izmeni profil', icon: MdEdit, onClick: () => navigate('/dashboard/profile/edit') },
      { label: t('profile.messages') || 'Poruke', icon: MdMessage, onClick: () => navigate('/dashboard/conversations') },
    ];
  }, [isParent, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = getAuthToken();
      const meRes = token ? await api.get('/auth/me').catch(() => null) : null;
      const currentUser = meRes?.data?.user ? meRes.data.user : getUser();
      if (meRes?.data?.user && token) {
        saveAuth(token, meRes.data.user);
      }
      setUser(currentUser);

      const [
        jobsResponse,
        friendsResponse,
        unpaidDebtResponse,
        manualDatesResponse
      ] = await Promise.all([
        api.get('/jobs').catch(() => ({ data: { jobs: [] } })),
        api.get('/friends').catch(() => ({ data: [] })),
        api.get('/payments/unpaid-debt').catch(() => ({ data: null })),
        api.get('/calendar-entries/manual-payment-dates').catch(() => ({ data: { manualPaymentDates: [] } }))
      ]);

      const jobs = jobsResponse.data?.jobs || (Array.isArray(jobsResponse.data) ? jobsResponse.data : []);
      setJobs(jobs);
      const friendsData = Array.isArray(friendsResponse.data) ? friendsResponse.data : [];
      setFriends(friendsData);
      setUnpaidDebt(unpaidDebtResponse?.data || null);

      const manualDatesRaw = manualDatesResponse?.data?.manualPaymentDateTimes
        || manualDatesResponse?.data?.manualPaymentDates
        || [];
      const normalizedManualDates = (manualDatesRaw || []).map((item) => {
        if (!item) return null;
        if (typeof item === 'string') {
          return { date: item.split('T')[0], time: '14:00' };
        }
        const rawDate = item.date || item.paymentDate || item.payment_date;
        if (!rawDate) return null;
        const normalizedDate = typeof rawDate === 'string'
          ? rawDate.split('T')[0]
          : rawDate instanceof Date
            ? rawDate.toISOString().split('T')[0]
            : null;
        if (!normalizedDate) return null;
        const rawTime = item.time || item.paymentTime || item.payment_time || '14:00';
        const cleanedTime = typeof rawTime === 'string' && /^\d{2}:\d{2}/.test(rawTime)
          ? rawTime.slice(0, 5)
          : '14:00';
        return { date: normalizedDate, time: cleanedTime };
      }).filter(Boolean);
      setManualPaymentDates(normalizedManualDates);
      
      const userJobs = jobs.filter(job => 
        job.parentId === currentUser.id || 
        job.babysitterId === currentUser.id ||
        job.serviceUserId === currentUser.id ||
        job.serviceProviderId === currentUser.id
      );

      setStats({
        totalJobs: userJobs.length,
        earnings: userJobs
          .filter(j => j.paymentStatus === 'paid')
          .reduce((sum, j) => sum + (j.totalAmount || 0), 0),
        completed: userJobs.filter(j => j.status === 'completed').length,
        rating: currentUser?.rating?.average || 0
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6 space-y-6 max-w-7xl mx-auto w-full">
        {/* Profile Header */}
        <div className="hidden lg:block">
          <div className="surface-panel flex items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-primary-600/10 flex items-center justify-center">
                <MdPerson className="text-primary-600 dark:text-primary-400 text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('profile.title') || 'Profil'}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('profile.subtitle') || 'Pregled naloga, statistika i brze akcije.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard/profile/edit')}
              className="btn-primary text-sm px-4 py-2"
            >
              {t('profile.edit') || 'Izmeni profil'}
            </button>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4 lg:gap-6">
            <div className="w-16 h-16 lg:w-24 lg:h-24 rounded-2xl bg-primary-600 flex items-center justify-center text-white text-2xl lg:text-3xl font-bold flex-shrink-0 overflow-hidden ring-4 ring-primary-100 dark:ring-primary-900/40">
              {user?.photoUrl || user?.profileImage ? (
                <img src={user.photoUrl || user.profileImage} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                user?.firstName?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                {user?.firstName} {user?.lastName}
                {user?.role && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold">
                    {user.role === 'parent' ? (t('profile.parent') || 'Roditelj') : user.role === 'babysitter' ? (t('profile.babysitter') || 'Bebisiterka') : user.role}
                  </span>
                )}
              </h2>
              <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1 truncate">
                {user?.email}
              </p>
              {user?.phone && (
                <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 truncate">
                  {user.phone}
                </p>
              )}
              <button
                onClick={() => navigate('/dashboard/profile/edit')}
                className="mt-4 btn-secondary flex items-center gap-2 w-fit text-sm lg:text-base lg:hidden"
              >
                <MdEdit />
                {t('profile.edit') || 'Edit Profile'}
              </button>
            </div>
          </div>
        </div>

        {/* Premium / Pretplata */}
        <div className="card flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <MdCardMembership className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {user?.hasPremium ? (t('profile.premiumActive') || 'Premium aktiviran') : (t('profile.premiumInactive') || 'Pretplata za dopisivanje')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {user?.hasPremium
                  ? (t('profile.premiumActiveDesc') || 'Možete neograničeno da šaljete poruke i kontaktirate korisnike.')
                  : (t('profile.premiumInactiveDesc') || 'Aktivirajte pretplatu da biste mogli da šaljete poruke i dopisujete se sa dadiljama ili roditeljima.')}
              </p>
            </div>
          </div>
          {user?.hasPremium ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium">
              <MdCheckCircle className="text-lg" />
              {t('profile.active') || 'Aktivan'}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/dashboard?tab=pricing')}
              className="btn-primary text-sm px-4 py-2 whitespace-nowrap"
            >
              {t('profile.activatePremium') || 'Aktiviraj Premium'}
            </button>
          )}
        </div>

      {/* Highlights */}
      {isParent ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdPeople className="text-2xl text-primary-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.activeBabysitters') || 'Aktivne dadilje'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-primary-600">
              {activeBabysitters}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdPayment className="text-2xl text-green-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.currentDebt') || 'Trenutno dugovanje'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {unpaidDebt?.totalDebt ? `${unpaidDebt.totalDebt.toFixed(0)} RSD` : '0 RSD'}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdCalendarToday className="text-2xl text-amber-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.nextPayment') || 'Sledeće plaćanje'}
              </h3>
            </div>
            <p className="text-lg font-semibold text-amber-700 dark:text-amber-300">
              {nextManualPayment
                ? nextManualPayment.toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' })
                : (t('profile.notSet') || 'Nije podešeno')}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdWork className="text-2xl text-primary-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.activeJobs') || 'Aktivni poslovi'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-primary-600">
              {activeJobsCount}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdCheckCircle className="text-2xl text-indigo-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.completed') || 'Završeno'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-indigo-600">
              {stats.completed}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdAttachMoney className="text-2xl text-green-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.earnings') || 'Zarada'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {stats.earnings.toFixed(0)} RSD
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdStar className="text-2xl text-yellow-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.rating') || 'Ocena'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-yellow-600">
              {stats.rating > 0 ? stats.rating.toFixed(1) : 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('profile.quickActions') || 'Brze akcije'}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={`${action.label}-${index}`}
                onClick={action.onClick}
                className="card text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                    <Icon className="text-xl text-primary-600" />
                  </div>
                  <span className="text-gray-400">→</span>
                </div>
                <div className="mt-3">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {action.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('profile.openSection') || 'Otvori sekciju'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* User Information */}
      {user && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
            {t('profile.information') || 'Informacije'}
          </h2>
          <div className="card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t('profile.role') || 'Uloga'}
                </label>
                <p className="text-gray-900 dark:text-gray-100 font-semibold capitalize mt-2">
                  {user.role === 'parent' ? (t('profile.parent') || 'Roditelj') : user.role === 'babysitter' ? (t('profile.babysitter') || 'Bebisiterka') : user.role}
                </p>
              </div>

              {user.hourlyRate && (
                <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('profile.hourlyRate') || 'Satnica'}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 font-semibold mt-2">
                    {user.hourlyRate} RSD/h
                  </p>
                </div>
              )}

              {user.phone && (
                <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('profile.phone') || 'Telefon'}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 mt-2">
                    {user.phone}
                  </p>
                </div>
              )}

              {user.address && (
                <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('profile.address') || 'Adresa'}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 mt-2">
                    {user.address.street && `${user.address.street}, `}
                    {user.address.city}
                    {user.address.postalCode && ` ${user.address.postalCode}`}
                  </p>
                </div>
              )}
            </div>

            {user.cities && user.cities.length > 0 && (
              <div className="mt-4 rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t('profile.cities') || 'Gradovi'}
                </label>
                <p className="text-gray-900 dark:text-gray-100 mt-2">
                  {user.cities.join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

